import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Bookmark, ChevronLeft, ChevronRight, FileText, Flag, Heart, Image as ImageIcon, MessageSquare, Mic, MoreHorizontal, Pin, Repeat2, Send, Smile, Square, Trash2, Video, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdate, fdatetime, fcfa } from "@/lib/format";
import { fileUrl, uploadFile, uploadVideo } from "@/lib/upload";
import { useAuth, can } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import { Avatar, StatusBadge } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export const FILTERS = [["tout", "Tout"], ["recherche", "Je recherche"], ["operations", "Opérations"], ["annonces", "Annonces"], ["secteurs", "Mes secteurs"], ["enregistres", "Enregistrés"]];
const EMOJIS = ["👍", "🙏", "🔥", "🤝", "💼", "📦", "🚚", "✅", "🎉", "💡"];
const MAX_VIDEO = 100 * 1024 * 1024;

function useUrl(id) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let live = true;
    setUrl(null);
    fileUrl(id).then((u) => live && setUrl(u)).catch(() => {});
    return () => {
      live = false;
    };
  }, [id]);
  return url;
}

function Thumb({ m, onOpen, className }) {
  const url = useUrl(m.id);
  if (!url) return <div className={`bg-mr-raised animate-pulse ${className}`} />;
  if (m.kind === "photo") return <button type="button" data-testid="post-photo" onClick={onOpen} className={`block overflow-hidden ${className}`}><img src={url} alt="" loading="lazy" className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-300" /></button>;
  if (m.kind === "video") return <video data-testid="post-video" src={url} controls playsInline preload="metadata" className={`bg-black ${className}`} />;
  if (m.kind === "audio") return <audio src={url} controls className="w-full" />;
  return <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm border border-mr-border rounded-[3px] p-3"><FileText size={16} />{m.filename}</a>;
}

function Lightbox({ photos, index, onClose }) {
  const [i, setI] = useState(index);
  const url = useUrl(photos[i].id);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl p-0 bg-black border-0" data-testid="media-lightbox">
        <DialogTitle className="sr-only">Photo {i + 1} sur {photos.length}</DialogTitle>
        <div className="relative grid place-items-center min-h-[50vh]">
          {url && <img src={url} alt="" className="max-h-[85vh] w-auto object-contain" />}
          {photos.length > 1 && <>
            <button aria-label="Précédente" data-testid="lightbox-prev" className="absolute left-2 w-10 h-10 grid place-items-center rounded-full bg-black/60 text-white" onClick={() => setI((i - 1 + photos.length) % photos.length)}><ChevronLeft size={20} /></button>
            <button aria-label="Suivante" data-testid="lightbox-next" className="absolute right-2 w-10 h-10 grid place-items-center rounded-full bg-black/60 text-white" onClick={() => setI((i + 1) % photos.length)}><ChevronRight size={20} /></button>
            <span className="absolute bottom-3 text-xs text-white/80 tabular">{i + 1} / {photos.length}</span>
          </>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function MediaGrid({ media }) {
  const [open, setOpen] = useState(null);
  const photos = media.filter((m) => m.kind === "photo");
  const others = media.filter((m) => m.kind !== "photo");
  const shown = photos.slice(0, 4);
  const cols = shown.length === 1 ? "grid-cols-1" : "grid-cols-2";
  return (
    <div className="space-y-2" data-testid="post-media">
      {shown.length > 0 && (
        <div className={`grid ${cols} gap-1 rounded-[3px] overflow-hidden`}>
          {shown.map((m, k) => (
            <div key={m.id} className={`relative ${shown.length === 3 && k === 0 ? "row-span-2" : ""}`}>
              <Thumb m={m} onOpen={() => setOpen(k)} className={`w-full ${shown.length === 1 ? "max-h-[520px] aspect-[4/3]" : shown.length === 3 && k === 0 ? "h-full" : "aspect-square"}`} />
              {k === 3 && photos.length > 4 && <button type="button" onClick={() => setOpen(3)} className="absolute inset-0 bg-black/55 text-white text-xl font-semibold grid place-items-center">+{photos.length - 4}</button>}
            </div>
          ))}
        </div>
      )}
      {others.map((m) => <Thumb key={m.id} m={m} className="w-full max-h-[520px] rounded-[3px]" />)}
      {open !== null && <Lightbox photos={photos} index={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function MentionInput({ value, onChange, mentions, setMentions, placeholder, testId, rows = 3 }) {
  const [sugg, setSugg] = useState([]);
  const change = async (v) => {
    onChange(v);
    const m = v.match(/@([\p{L}' -]{2,20})$/u);
    if (m) {
      const { data } = await api.get("/search", { params: { q: m[1] } }).catch(() => ({ data: { members: [] } }));
      setSugg(data.members.slice(0, 5));
    } else setSugg([]);
  };
  const pick = (u) => {
    onChange(value.replace(/@([\p{L}' -]{2,20})$/u, `@${u.name} `));
    setMentions([...new Set([...mentions, u.id])]);
    setSugg([]);
  };
  return (
    <div className="relative">
      <textarea data-testid={testId} rows={rows} className="mr-input resize-none" placeholder={placeholder} value={value} onChange={(e) => change(e.target.value)} />
      {sugg.length > 0 && <ul className="absolute z-20 left-0 right-0 mt-1 bg-mr-surface border border-mr-border rounded-[3px]">{sugg.map((u) => (
        <li key={u.id}><button type="button" data-testid="mention-suggestion" className="w-full text-left px-3 py-2 text-sm hover:bg-mr-raised" onClick={() => pick(u)}>{u.name} <span className="text-mr-muted">· {u.company}</span></button></li>))}</ul>}
    </div>
  );
}

function Recorder({ onDone }) {
  const [rec, setRec] = useState(null);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const r = new MediaRecorder(stream);
      const chunks = [];
      r.ondataavailable = (e) => chunks.push(e.data);
      r.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        onDone(new File([new Blob(chunks, { type: "audio/webm" })], "note-vocale.webm", { type: "audio/webm" }));
      };
      r.start();
      setRec(r);
    } catch {
      toast.error("Micro indisponible sur cet appareil.");
    }
  };
  return rec ? <button type="button" data-testid="composer-voice-stop" className="mr-btn-danger px-3" onClick={() => { rec.stop(); setRec(null); }}><Square size={15} /> Arrêter</button>
    : <button type="button" data-testid="composer-voice-button" className="mr-btn-ghost px-3" onClick={start} aria-label="Note vocale"><Mic size={16} /></button>;
}

function FilePreview({ f, onRemove }) {
  const [src] = useState(() => (f.kind === "photo" || f.kind === "video" ? URL.createObjectURL(f.file) : null));
  return (
    <div className="relative w-20 h-20 shrink-0 rounded-[3px] overflow-hidden border border-mr-border bg-mr-raised grid place-items-center" data-testid="composer-file-preview">
      {f.kind === "photo" && <img src={src} alt="" className="w-full h-full object-cover" />}
      {f.kind === "video" && <video src={src} muted className="w-full h-full object-cover" />}
      {!src && <span className="text-[10px] text-mr-text2 p-1 text-center break-all">{f.file.name}</span>}
      {f.kind === "video" && <Video size={14} className="absolute bottom-1 left-1 text-white drop-shadow" />}
      <button type="button" aria-label="Retirer" data-testid="composer-file-remove" onClick={onRemove} className="absolute top-1 right-1 w-5 h-5 grid place-items-center rounded-full bg-black/70 text-white"><X size={12} /></button>
    </div>
  );
}

export function Composer({ onPosted }) {
  const { me } = useAuth();
  const { settings } = useConfig();
  const [type, setType] = useState("publication");
  const [kind, setKind] = useState("");
  const [content, setContent] = useState("");
  const [mentions, setMentions] = useState([]);
  const [files, setFiles] = useState([]);
  const [opId, setOpId] = useState("");
  const [ops, setOps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [emoji, setEmoji] = useState(false);
  const fileRef = useRef(null);
  const staff = can(me, "moderation:manage");
  useEffect(() => {
    if (type === "operation") api.get("/operations").then(({ data }) => setOps(Array.isArray(data) ? data : data.items || [])).catch(() => {});
  }, [type]);
  const kindOf = (f) => (f.type.startsWith("image/") ? "photo" : f.type.startsWith("video/") ? "video" : f.type.startsWith("audio/") ? "audio" : "document");
  const add = (list) => {
    const next = [...files, ...Array.from(list).map((f) => ({ file: f, kind: kindOf(f) }))];
    if (next.filter((f) => f.kind === "photo").length > 10) return toast.error("10 photos maximum par publication.");
    if (next.filter((f) => f.kind === "video").length > 1) return toast.error("Une seule vidéo par publication.");
    if (next.some((f) => f.kind === "video" && f.file.size > MAX_VIDEO)) return toast.error("Vidéo trop lourde (100 Mo maximum).");
    setFiles(next);
  };
  const submit = async () => {
    setBusy(true);
    try {
      const ids = [];
      for (const f of files) {
        if (f.kind === "video") {
          setProgress(0);
          ids.push(await uploadVideo(f.file, setProgress));
          setProgress(null);
        } else ids.push(await uploadFile(f.kind, f.file));
      }
      const { data } = await api.post("/feed", { type, search_kind: kind, content, media_file_ids: ids, mention_ids: mentions, operation_id: opId || null });
      setContent(""); setFiles([]); setMentions([]); setKind(""); setOpId(""); setType("publication");
      onPosted(data);
      toast.success("Publication en ligne");
    } catch (e) {
      toast.error(e.response ? errMsg(e) : e.message);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };
  const types = [["publication", "Publication"], ["recherche", "Je recherche"], ["operation", "Partager une opération"], ...(staff ? [["annonce", "Annonce officielle"]] : [])];
  return (
    <div className="mr-card space-y-3" data-testid="feed-composer">
      <div className="flex gap-3 items-start">
        <Avatar name={me.user.name} fileId={me.user.photo_file_id} size={40} />
        <div className="flex-1 min-w-0 space-y-3">
          <MentionInput testId="composer-content-input" value={content} onChange={setContent} mentions={mentions} setMentions={setMentions} placeholder={`Quoi de neuf, ${me.user.name.split(" ")[0]} ? **gras**, _italique_, @mention`} />
          <div className="flex gap-2 overflow-x-auto">{types.map(([v, l]) => (
            <button key={v} type="button" data-testid={`composer-type-${v}`} onClick={() => setType(v)} className={`shrink-0 px-3 h-8 rounded-full border text-xs transition-colors ${type === v ? "border-mr-accent text-mr-accent" : "border-mr-border text-mr-text2 hover:border-mr-text2"}`}>{l}</button>))}</div>
          {type === "recherche" && <select data-testid="composer-search-kind" className="mr-input" value={kind} onChange={(e) => setKind(e.target.value)}><option value="">Je recherche un…</option>{(settings["catalog.search_kinds"] || []).map((k) => <option key={k}>{k}</option>)}</select>}
          {type === "operation" && <select data-testid="composer-operation-select" className="mr-input" value={opId} onChange={(e) => setOpId(e.target.value)}><option value="">Choisir l'opération…</option>{ops.map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}</select>}
          {emoji && <div className="flex flex-wrap gap-1" data-testid="composer-emoji-panel">{EMOJIS.map((e) => <button key={e} type="button" className="w-9 h-9 text-lg" onClick={() => setContent(content + e)}>{e}</button>)}</div>}
          {files.length > 0 && <div className="flex gap-2 overflow-x-auto pb-1" data-testid="composer-files">{files.map((f, k) => <FilePreview key={`${f.file.name}-${k}`} f={f} onRemove={() => setFiles(files.filter((_, j) => j !== k))} />)}</div>}
          {progress !== null && (
            <div data-testid="composer-upload-progress"><div className="h-1.5 bg-mr-raised rounded-full overflow-hidden"><div className="h-full bg-mr-accent transition-[width] duration-300" style={{ width: `${progress}%` }} /></div>
              <p className="text-xs text-mr-muted mt-1 tabular">Envoi de la vidéo… {progress} %</p></div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-mr-border pt-3">
        <div className="flex items-center">
          <button type="button" className="mr-btn-ghost px-3" aria-label="Photos ou vidéo" data-testid="composer-media-button" onClick={() => fileRef.current.click()}><ImageIcon size={16} /><span className="hidden sm:inline text-xs">Photo / vidéo</span></button>
          <input ref={fileRef} type="file" multiple accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx" className="hidden" data-testid="composer-file-input" onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
          <Recorder onDone={(f) => setFiles([...files, { file: f, kind: "audio" }])} />
          <button type="button" className="mr-btn-ghost px-3" aria-label="Émojis" data-testid="composer-emoji-button" onClick={() => setEmoji(!emoji)}><Smile size={16} /></button>
        </div>
        <button data-testid="composer-submit-button" className="mr-btn-primary px-4" disabled={busy || (type === "operation" ? !opId : !content.trim() && !files.length)} onClick={submit}><Send size={15} /> {busy ? "Envoi…" : "Publier"}</button>
      </div>
    </div>
  );
}

function Comments({ post, onCount }) {
  const { me } = useAuth();
  const [items, setItems] = useState(null);
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]);
  const [reply, setReply] = useState(null);
  const load = () => api.get(`/feed/${post.id}/comments`).then(({ data }) => { setItems(data); onCount(data.length); });
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async () => {
    try {
      await api.post(`/feed/${post.id}/comments`, { content: text, parent_id: reply?.id || null, mention_ids: mentions });
      setText(""); setReply(null); setMentions([]);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!items) return null;
  const roots = items.filter((c) => !c.parent_id);
  const row = (c, child) => (
    <li key={c.id} className={`flex gap-2 ${child ? "ml-9" : ""}`} data-testid="comment-row">
      <Avatar name={c.author_name} size={child ? 24 : 28} />
      <div className="min-w-0">
        <div className="bg-mr-raised rounded-[3px] px-3 py-2 text-sm"><span className="font-medium">{c.author_name}</span> <span className="text-mr-text2 whitespace-pre-wrap">{c.content}</span></div>
        <p className="text-[11px] text-mr-muted mt-1 tabular">{fdatetime(c.created_at)}{!child && <button className="ml-3 hover:text-mr-text" data-testid="comment-reply-button" onClick={() => setReply(c)}>Répondre</button>}</p>
      </div>
    </li>
  );
  return (
    <div className="border-t border-mr-border pt-3 space-y-3" data-testid="comments-panel">
      <ul className="space-y-3">{roots.map((c) => [row(c, false), ...items.filter((x) => x.parent_id === c.id).map((x) => row(x, true))])}</ul>
      {reply && <p className="text-xs text-mr-muted">Réponse à {reply.author_name} · <button onClick={() => setReply(null)}>annuler</button></p>}
      {can(me, "feed:post") && <div className="flex gap-2 items-start">
        <Avatar name={me.user.name} fileId={me.user.photo_file_id} size={28} />
        <div className="flex-1"><MentionInput testId="comment-input" rows={1} value={text} onChange={setText} mentions={mentions} setMentions={setMentions} placeholder="Écrire un commentaire…" /></div>
        <button data-testid="comment-send-button" className="mr-btn-secondary px-3" disabled={!text.trim()} onClick={send} aria-label="Envoyer"><Send size={15} /></button>
      </div>}
    </div>
  );
}

function AuthorLine({ p, small }) {
  return (
    <Link to={`/app/reseau/${p.author.id}`} className="flex items-center gap-3 min-w-0" data-testid="post-author-link">
      <Avatar name={p.author.name} fileId={p.author.photo_file_id} size={small ? 30 : 42} />
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">{p.author.name}</p>
        <p className="text-xs text-mr-muted truncate">{p.author.headline ? `${p.author.headline} · ` : ""}<span className="tabular">{fdatetime(p.created_at)}</span></p>
      </div>
    </Link>
  );
}

function Bar({ value, max, testId }) {
  const pct = max ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return <div className="h-1.5 bg-mr-raised rounded-full overflow-hidden" data-testid={testId} aria-valuenow={pct} role="progressbar"><div className="h-full bg-mr-accent transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>;
}

export function OperationProgressCard({ op }) {
  const g = op.progress || {};
  return (
    <Link to={`/app/operations/${op.id}`} className="block border border-mr-border rounded-[3px] p-4 space-y-4 hover:border-mr-accent transition-colors" data-testid="post-operation-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0"><p className="text-xs uppercase tracking-wider text-mr-accent">Opération</p><p className="font-medium mt-1">{op.title}</p></div>
        {g.step_label && <span className="shrink-0 text-xs border border-mr-border rounded-full px-2.5 py-1 text-mr-text2" data-testid="op-card-step">{g.step_label}</span>}
      </div>
      {g.shares_total > 0 && <div className="space-y-1.5"><div className="flex justify-between text-xs"><span className="text-mr-muted">Places</span><span className="tabular" data-testid="op-card-places">{g.shares_taken} / {g.shares_total}</span></div><Bar value={g.shares_taken} max={g.shares_total} testId="op-card-places-bar" /></div>}
      {g.target > 0 && <div className="space-y-1.5"><div className="flex flex-wrap justify-between gap-x-3 text-xs"><span className="text-mr-muted">Montant réuni</span><span className="tabular" data-testid="op-card-amount">{fcfa(g.collected)} / {fcfa(g.target)}</span></div><Bar value={g.collected} max={g.target} testId="op-card-amount-bar" /></div>}
      {g.deadline && <p className="text-xs text-mr-text2" data-testid="op-card-deadline">Date limite : {fdate(g.deadline)} · <span className={g.days_left <= 3 ? "text-mr-warning" : ""}>{g.days_left === 0 ? "dernier jour" : `${g.days_left} jour${g.days_left > 1 ? "s" : ""} restant${g.days_left > 1 ? "s" : ""}`}</span></p>}
    </Link>
  );
}

function Body({ p }) {
  return (
    <>
      {p.content && <div className="text-[15px] text-mr-text2 prose-sm [&_strong]:text-mr-text [&_ul]:list-disc [&_ul]:pl-5 break-words" data-testid="post-content"><ReactMarkdown>{p.content}</ReactMarkdown></div>}
      {p.operation && <OperationProgressCard op={p.operation} />}
      {p.media?.length > 0 && <MediaGrid media={p.media} />}
    </>
  );
}

function RepostDialog({ post, onClose, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const src = post.original || post;
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/feed/${post.id}/repost`, { content: text });
      toast.success("Publication repartagée dans le fil");
      onDone(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="repost-dialog">
        <DialogHeader><DialogTitle>Repartager dans le fil</DialogTitle></DialogHeader>
        <textarea data-testid="repost-comment-input" rows={3} className="mr-input resize-none" placeholder="Ajouter un mot (facultatif)" value={text} onChange={(e) => setText(e.target.value)} />
        <div className="border border-mr-border rounded-[3px] p-3 text-sm text-mr-text2 max-h-40 overflow-hidden"><p className="font-medium text-mr-text mb-1">{src.author.name}</p>{src.content?.slice(0, 220) || "Publication avec médias"}</div>
        <button data-testid="repost-confirm-button" className="mr-btn-primary" disabled={busy} onClick={go}><Repeat2 size={16} /> {busy ? "Partage…" : "Repartager"}</button>
      </DialogContent>
    </Dialog>
  );
}

export function PostCard({ post, onChange, onReposted }) {
  const { me } = useAuth();
  const [p, setP] = useState(post);
  const [open, setOpen] = useState(false);
  const [reposting, setReposting] = useState(false);
  const act = async (fn) => {
    try {
      await fn();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const like = () => act(async () => {
    const { data } = await api.post(`/feed/${p.id}/react`, { kind: "like" });
    const r = { ...p.reactions };
    if (p.my_reaction) r[p.my_reaction] = (r[p.my_reaction] || 1) - 1;
    if (data.my_reaction) r[data.my_reaction] = (r[data.my_reaction] || 0) + 1;
    setP({ ...p, reactions: r, my_reaction: data.my_reaction });
  });
  const save = () => act(async () => { const { data } = await api.post(`/feed/${p.id}/save`); setP({ ...p, saved: data.saved }); toast.success(data.saved ? "Publication enregistrée" : "Retirée des enregistrements"); });
  const report = () => act(async () => { const reason = window.prompt("Motif du signalement"); if (reason) { await api.post(`/feed/${p.id}/report`, { reason }); toast.success("Signalement transmis"); } });
  const share = () => act(async () => {
    const q = window.prompt("Envoyer à quel membre ? (nom)");
    if (!q) return;
    const { data } = await api.get("/search", { params: { q } });
    if (!data.members.length) return toast.error("Aucun membre trouvé");
    await api.post(`/feed/${p.id}/share`, { member_id: data.members[0].id });
    toast.success(`Envoyé à ${data.members[0].name}`);
  });
  const pin = () => act(async () => { const { data } = await api.post(`/feed/${p.id}/pin`); setP({ ...p, pinned: data.pinned }); });
  const del = () => act(async () => { if (window.confirm("Supprimer cette publication ?")) { await api.delete(`/feed/${p.id}`); onChange?.(); } });
  const likes = Object.values(p.reactions || {}).reduce((a, b) => a + b, 0);
  const repost = p.type === "partage";
  return (
    <li className="mr-card space-y-3" data-testid="feed-post">
      {repost && <p className="text-xs text-mr-muted flex items-center gap-1.5 -mb-1" data-testid="post-repost-label"><Repeat2 size={13} /> {p.author.name} a repartagé</p>}
      <div className="flex items-center justify-between gap-3">
        <AuthorLine p={p} />
        <div className="flex items-center gap-2 shrink-0">
          {p.pinned && <Pin size={14} className="text-mr-accent" data-testid="post-pinned-icon" />}
          {p.type === "annonce" && <StatusBadge status="annonce" label="Annonce du bureau" tone="info" />}
          {p.type === "recherche" && <StatusBadge status="recherche" label={`Je recherche · ${p.search_kind}`} tone="warning" />}
          <DropdownMenu>
            <DropdownMenuTrigger asChild><button className="mr-btn-ghost px-2" aria-label="Plus d'options" data-testid="post-menu-button"><MoreHorizontal size={16} /></button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem data-testid="post-share-button" onClick={share}><Send size={14} className="mr-2" /> Envoyer à un membre</DropdownMenuItem>
              <DropdownMenuItem data-testid="post-report-button" onClick={report}><Flag size={14} className="mr-2" /> Signaler</DropdownMenuItem>
              {p.type === "annonce" && can(me, "moderation:manage") && <DropdownMenuItem data-testid="post-pin-button" onClick={pin}><Pin size={14} className="mr-2" /> {p.pinned ? "Désépingler" : "Épingler"}</DropdownMenuItem>}
              {p.can_delete && <DropdownMenuItem data-testid="post-delete-button" onClick={del} className="text-mr-danger"><Trash2 size={14} className="mr-2" /> Supprimer</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <Body p={p} />
      {repost && (p.original ? (
        <div className="border border-mr-border rounded-[3px] p-4 space-y-3" data-testid="post-original">
          <AuthorLine p={p.original} small />
          <Body p={p.original} />
        </div>
      ) : <p className="text-sm text-mr-muted border border-dashed border-mr-border rounded-[3px] p-4" data-testid="post-original-missing">La publication d'origine n'est plus disponible.</p>)}
      {(likes > 0 || p.comments_count > 0 || p.reposts_count > 0) && (
        <div className="flex justify-between text-xs text-mr-muted tabular" data-testid="post-counters">
          <span>{likes > 0 && `${likes} j'aime`}</span>
          <span>{p.comments_count > 0 && `${p.comments_count} commentaire${p.comments_count > 1 ? "s" : ""}`}{p.reposts_count > 0 && ` · ${p.reposts_count} partage${p.reposts_count > 1 ? "s" : ""}`}</span>
        </div>
      )}
      <div className="grid grid-cols-4 border-t border-mr-border pt-2 -mb-1 text-mr-text2">
        <button className={`mr-btn-ghost px-2 ${p.my_reaction ? "text-mr-accent" : ""}`} data-testid="post-react-like" onClick={like} disabled={!can(me, "feed:post")} aria-pressed={!!p.my_reaction}><Heart size={16} className={p.my_reaction ? "fill-current" : ""} /><span className="hidden sm:inline text-xs">J'aime</span></button>
        <button className="mr-btn-ghost px-2" data-testid="post-comments-button" onClick={() => setOpen(!open)}><MessageSquare size={16} /><span className="hidden sm:inline text-xs">Commenter</span></button>
        <button className="mr-btn-ghost px-2" data-testid="post-repost-button" onClick={() => setReposting(true)} disabled={!can(me, "feed:post")}><Repeat2 size={16} /><span className="hidden sm:inline text-xs">Repartager</span></button>
        <button className={`mr-btn-ghost px-2 ${p.saved ? "text-mr-accent" : ""}`} data-testid="post-save-button" onClick={save} aria-label="Enregistrer"><Bookmark size={16} className={p.saved ? "fill-current" : ""} /><span className="hidden sm:inline text-xs">{p.saved ? "Enregistré" : "Enregistrer"}</span></button>
      </div>
      {open && <Comments post={p} onCount={(n) => setP((x) => ({ ...x, comments_count: n }))} />}
      {reposting && <RepostDialog post={p} onClose={() => setReposting(false)} onDone={(d) => { setReposting(false); setP({ ...p, reposts_count: (p.reposts_count || 0) + 1 }); onReposted?.(d); }} />}
    </li>
  );
}
